import { Button, Card, Progress, Space, Statistic, Typography } from "antd";
import { AlertCircle, CheckCircle, FileText } from "lucide-react";
import type React from "react";
import { FixingLoader, FixingOverlay } from "@/components/ui/FixingLoader";
import { useApplyArticleSchema } from "@/hooks/useSchemaManagement";
import type { ArticleSchemaStatus } from "@/types/schema";

const { Text } = Typography;

interface ArticleSchemaCardProps {
	stats?: ArticleSchemaStatus;
	onSelectItems?: () => void;
}

export const ArticleSchemaCard: React.FC<ArticleSchemaCardProps> = ({
	stats,
	onSelectItems,
}) => {
	const applyArticleSchema = useApplyArticleSchema();

	if (!stats) {
		return (
			<Card loading>
				<div style={{ height: 200 }} />
			</Card>
		);
	}

	const progressPercent =
		stats.total_applicable > 0
			? Math.round((stats.with_schema / stats.total_applicable) * 100)
			: 0;

	const isComplete = stats.without_schema === 0;
	const isRunning = applyArticleSchema.isPending;

	return (
		<Card
			title={
				<Space>
					<FileText size={20} />
					<span>Article Schema</span>
				</Space>
			}
			extra={
				isComplete ? (
					<CheckCircle size={20} style={{ color: "#52c41a" }} />
				) : (
					<AlertCircle size={20} style={{ color: "#faad14" }} />
				)
			}
		>
			<div className="relative">
			<FixingOverlay
				show={applyArticleSchema.isPending}
				label="Applying Article Schema…"
			/>
			<Space direction="vertical" size={16} style={{ width: "100%" }}>
				{/* Progress Bar */}
				<div>
					<div
						style={{
							marginBottom: 8,
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<Text type="secondary">Implementation Progress</Text>
						<Text strong>{progressPercent}%</Text>
					</div>
					<Progress
						percent={progressPercent}
						status={isComplete ? "success" : "active"}
						strokeColor={isComplete ? "#52c41a" : "#1677ff"}
					/>
				</div>

				{/* Statistics */}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "1fr 1fr 1fr",
						gap: 16,
					}}
				>
					<Statistic
						title="Total Posts"
						value={stats.total_applicable}
						valueStyle={{ fontSize: 20 }}
					/>
					<Statistic
						title="With Schema"
						value={stats.with_schema}
						valueStyle={{ fontSize: 20, color: "#52c41a" }}
					/>
					<Statistic
						title="Needing Schema"
						value={stats.without_schema}
						valueStyle={{
							fontSize: 20,
							color: stats.without_schema > 0 ? "#faad14" : "#52c41a",
						}}
					/>
				</div>

				{/* Action Buttons */}
				<Button
					type="primary"
					block
					size="large"
					onClick={() => applyArticleSchema.mutate()}
					disabled={isRunning || isComplete}
					icon={
						isRunning ? (
							<FixingLoader iconOnly label="Applying Article Schema…" />
						) : undefined
					}
				>
					{isRunning
						? "Applying Schema..."
						: isComplete
							? "All Posts Have Schema"
							: "Apply to All Posts"}
				</Button>

				{onSelectItems && (
					<Button
						block
						size="large"
						onClick={onSelectItems}
						disabled={isRunning}
					>
						Select Specific Posts
					</Button>
				)}

				{/* Help Text */}
				<Text type="secondary" style={{ fontSize: 12 }}>
					Article schema markup helps search engines understand your blog post
					structure and content type.
				</Text>
			</Space>
			</div>
		</Card>
	);
};

export default ArticleSchemaCard;
